/**
 * Права пользователя на UX-слое (DP-348). Сервер всё равно проверяет сам —
 * здесь только то, что решает, показывать ли элемент управления.
 */
import type { UserInfo } from '../types/user';

/**
 * Можно ли вводить данные.
 *
 * Уровней доступа три (просмотр / ввод / супер-админ), и различает их
 * `is_editor`. Пока поля нет в ответе — бэкенд старее фронта — работает прежнее
 * правило «есть КАТО-роль либо админ». Без этого fallback'а фронт, уехавший на
 * стенд раньше бэкенда, отобрал бы ввод у всех, кто им сейчас пользуется.
 */
export const canEditData = (user: UserInfo | null | undefined): boolean => {
  if (!user) return false;
  if (user.is_editor !== undefined) return user.is_editor;
  return user.is_admin || user.kato_access.some((code) => code !== 0);
};

/**
 * Покрывает ли выданный КАТО `root` территорию `kato` (включая сам `root`).
 *
 * Зеркало серверного `auth/models.py::kato_covers`: `kato_access` отдаёт корни
 * поддеревьев, поэтому сравнивать их с конкретным КАТО через `includes` нельзя
 * — район не совпадёт ни с одним из своих сёл.
 *
 * Разряды кода: РР ДД СС ННН. Уровень задаётся тем, какие младшие разряды
 * обнулены, поэтому сравниваем делением, а не префиксом строки.
 */
export const katoCovers = (root: number, kato: number): boolean => {
  for (const span of [10_000_000, 100_000, 1_000]) {
    if (root % span === 0) return Math.trunc(root / span) === Math.trunc(kato / span);
  }
  return root === kato;
};

/**
 * Доступен ли пользователю этот КАТО.
 *
 * Пустой `kato_access` — прав не знаем (аноним на открытом стенде), тогда не
 * ограничиваем: решает сервер. `[0]` — супер-админ, весь регион.
 */
export const isKatoInScope = (
  kato: number,
  katoAccess: number[] | undefined,
): boolean => {
  if (!katoAccess || katoAccess.length === 0) return true;
  if (katoAccess.includes(0)) return true;
  return katoAccess.some((root) => katoCovers(root, kato));
};
